import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent, trackHeroCTA } from '@/lib/analytics';

interface HeroSectionProps {
  onOpenApplicabilityModal: () => void;
}

export function HeroSection({ onOpenApplicabilityModal }: HeroSectionProps) {
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden py-24 md:py-36 min-h-[60vh] md:min-h-[70vh] flex items-center" style={{ contain: 'layout style' }}>
      {/* Static background gradients — no framer-motion needed */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/3 to-accent/15" />
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-accent/5 to-primary/10 opacity-80" />

      {/* Content */}
      <div className="relative z-10 container">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Badge — CSS animation instead of framer-motion */}
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium relative overflow-hidden hero-badge-bg">
            <span className="shrink-0 relative z-10 text-primary">
              <Shield className="w-4 h-4" />
            </span>
            <span className="relative z-10 text-primary">Consultoria Técnica Independente</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-foreground">
            Recebeu uma recomendação técnica para o seu elevador e não sabe se é mesmo necessária?
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Apoio técnico independente, baseado na{' '}
            <span className="text-foreground font-medium">EN 81</span>, para
            decidir com rigor antes de assumir custos, riscos ou
            responsabilidades legais.
          </p>

          <div className="pt-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4 sm:px-0">
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  trackEvent('applicability_modal_open', {
                    location: 'hero',
                    page_path: window.location.pathname,
                  });
                  trackHeroCTA('primary', 'Ver se este serviço se aplica ao meu caso');
                  onOpenApplicabilityModal();
                }}
                className="gap-2 text-base px-6 sm:px-8 py-5 sm:py-6 min-h-[56px] w-full sm:w-auto whitespace-normal text-center leading-tight touch-manipulation border-primary text-primary hover:bg-primary/5"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                Ver se este serviço se aplica ao meu caso
              </Button>

              <Button
                size="lg"
                onClick={() => navigate('/members/auth')}
                className="gap-2 text-base px-6 sm:px-8 py-5 sm:py-6 min-h-[56px] w-full sm:w-auto whitespace-normal text-center leading-tight touch-manipulation relative overflow-hidden"
              >
                <span className="flex items-center justify-center gap-2 flex-wrap">
                  Criar conta gratuita
                  <ArrowRight className="w-5 h-5 flex-shrink-0" />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
